// Scan Again camera for the QEW Tag page (NAV_AUDIT.md item 6, Robert 2026-10-22).
// The tag page's Scan Again only showed a pop-up. This is the same camera, look and rules as the Guest Page's
// Scan Again (guest-page.ts, approved 2026-10-13): read the next SafeTag label or bag tag and open it.
// Cancel puts the page back as it was, buttons and all.

import "./field-access.css";
import { startQrScanner, type QrScanner } from "./qr-scanner";
import { scanAgainTarget } from "./scan-again";
import { keepAdminBack } from "./admin-way-back";

const esc = (s: string) => s.replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]!);
const HOLD_MS = 1000;

export function scanAgainViewHtml(current: string): string {
  return `<div class="fa-wrap">
    <div class="fa-top"><div><div class="nm">Alltech</div><div class="pg">Electrical Safety Program</div></div></div>
    <div class="fa-body">
      <div class="fa-card"><h3>Scan Again</h3>
        <div class="fa-cam"><video id="sa-video" playsinline muted></video><div class="fr"></div><div class="tx" id="sa-cam-tx">Starting the camera…</div></div>
        <div class="fa-hint" id="sa-hint">Scan the QR on the next SafeTag label or bag tag.</div></div>
      <button class="fa-plain" type="button" data-sa="cancel">Cancel</button>
      ${current ? `<div class="fa-hint" style="text-align:left;padding:0 4px">Viewing ${esc(current)}.</div>` : ""}
    </div>
  </div>`;
}

/**
 * Opens the camera in `host`. Cancel puts the page back as it was and calls onCancel.
 * The page underneath is hidden, not copied (NAV_CRAWL_WORDS.md A6): its buttons keep working,
 * a typed name stays typed, and the scroll comes back to where it was.
 */
export function openScanAgain(host: HTMLElement, current: string, onCancel: () => void = () => undefined): void {
  const y = window.scrollY;
  const hidden = [...host.children].filter((c): c is HTMLElement => c instanceof HTMLElement).map((el) => ({ el, display: el.style.display }));
  for (const h of hidden) h.el.style.display = "none";
  const view = document.createElement("div");
  view.dataset.saView = "";
  view.innerHTML = scanAgainViewHtml(current);
  host.appendChild(view);
  window.scrollTo(0, 0);
  let scanner: QrScanner | null = null;
  let found = "";
  const close = () => {
    scanner?.stop();
    host.removeEventListener("click", onClick, true);
    view.remove();
    for (const h of hidden) h.el.style.display = h.display;
    window.scrollTo(0, y);
    onCancel();
  };
  const onClick = (ev: Event) => {
    if ((ev.target as HTMLElement).closest('[data-sa="cancel"]')) {
      ev.stopPropagation();
      close();
    }
  };
  host.addEventListener("click", onClick, true);
  const video = view.querySelector<HTMLVideoElement>("#sa-video");
  const tx = view.querySelector<HTMLElement>("#sa-cam-tx");
  const hint = view.querySelector<HTMLElement>("#sa-hint");
  if (!video || !tx || !hint) return;
  scanner = startQrScanner(
    video,
    (link) => {
      if (found) return false;
      const target = scanAgainTarget(link, location.host);
      if (target.kind === "refused") {
        hint.textContent = target.message;
        return false;
      }
      found = target.id;
      view.querySelector(".fa-cam")?.classList.add("found");
      tx.textContent = `Found ${target.id} ✓`;
      hint.textContent = "Read. Opening it.";
      window.setTimeout(() => location.assign(new URL(keepAdminBack(target.path, location.search), location.origin).href), HOLD_MS);
      return true;
    },
    (text) => {
      tx.textContent = text.startsWith("Looking") || text.startsWith("Starting") ? text : `${text} Use the phone camera instead.`;
    },
  );
}
